import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { UserService } from 'src/app/shared/services/user.service';
import { IconType } from 'src/app/modules/authentication/components/models/authentication-actions.enum';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { SidebarService } from 'src/app/shared/services/sidebar.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';
import { MinimizedMeetingService } from 'src/app/core/meetings/services/minimized-meeting.service';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavBarComponent implements OnInit, OnDestroy {
  isSidebarExpanded: boolean = true; // Default to expanded
  isMobileSidebarOpen: boolean = false;
  dragAndDropArea: HTMLElement | null = null;
  documentUploadInput!: HTMLInputElement;
  
  private readonly SIDEBAR_STATE_KEY = 'sidebarExpandedState';
  private destroy$ = new Subject<void>();
  private cdr = inject(ChangeDetectorRef);

  constructor(   
    private _router: Router,
    private dialogService: DialogService,
    public userService: UserService,
    public sidebarService: SidebarService,
    private signalRService: SignalRService,
    private minimizedMeetingService: MinimizedMeetingService
  ) {}

  // Roles that have restricted access
  private readonly RESTRICTED_ROLES = ['TCM', 'CHAIRMAN', 'VICECHAIRMAN', 'VICE CHAIRMAN', 'OBSERVER'];

  // Role name for Evaluators (matches backend UserRole.Evaluator.ToString())
  private readonly EVALUATOR_ROLE = 'Evaluator';
  private readonly PROCUREMENT_OFFICER_ROLES = ['PROCURMENTOFFICER', 'PROCUREMENTOFFICER', 'PROCURMENTHEAD', 'PROCUREMENTHEAD'];
  private readonly PROCUREMENT_OFFICER_MENU = new Set(['Dashboard', 'Manage Vendors', 'Tenders', 'Practices', 'FWA Practices', 'Bids']);

  sidebarItems = [
    {
      label: 'Dashboard',
      icon: 'dashbored',
      route: '/home',
      showForRestrictedRoles: true
    },
    {
      label: 'Purchase Requests',
      icon: 'notification-check.svg',
      route: '/approved-prs',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'Tenders',
      icon: 'Tenders',
      route: '/tender',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'Practices',
      icon: 'Tenders',
      route: '/practice',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'FWA Practices',
      icon: 'Tenders',
      route: '/fwa-practice',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'Bids',
      icon: 'Bids',
      route: '/bid',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'Evaluations',
      icon: 'edit-check.svg',
      route: '/evaluations',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'Meetings',
      icon: 'Messages.png',
      route: '/meetings',
      showForRestrictedRoles: true // TCM, Chairman, Vice Chairman can see this
    },
    {
      label: 'Minutes of Meeting',
      icon: 'minutes-of-meeting.svg',
      route: '/minutes-of-meeting',
      showForRestrictedRoles: true // TCM, Chairman, Vice Chairman can see this
    },
    {
      label: 'Agenda Items',
      icon: 'finanace.png',
      route: '/agenda-items',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'FAQs',
      icon: 'question-mark.svg',
      route: '/faqs',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'Manage Vendors',
      icon: 'finanace.png',
      route: '/manage-vendors',
      showForRestrictedRoles: false,
      allowedRoles: ['PROCURMENTOFFICER', 'PROCUREMENTOFFICER', 'SECRETARY', 'TENDER COMMITTEE SECRETARY', 'VICECHAIRMAN', 'VICE CHAIRMAN']
    },
    {
      label: 'Lookups',
      icon: 'finanace.png',
      route: '/lookups',
      showForRestrictedRoles: false // Hidden for restricted roles
    },
    {
      label: 'User Management',
      icon: 'dashbored',
      route: '/user-management',
      showForRestrictedRoles: false,
      allowedRoles: ['SECRETARY', 'TENDER COMMITTEE SECRETARY']
    },
    {
      label: 'logout',
      icon: 'logout',
      route: '/logout',
      showForRestrictedRoles: true // Everyone can see logout
    }
  ];

  ngOnInit(): void {
    // Load sidebar expand/collapse state from localStorage
    this.loadSidebarState();
    
    // Subscribe to sidebar state changes
    this.sidebarService.isMobileSidebarOpen$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      isOpen => {
        this.isMobileSidebarOpen = isOpen;
        // Prevent body scroll when sidebar is open on mobile
        if (window.innerWidth < 992) {
          if (isOpen) {
            document.body.classList.add('sidebar-open');
            // Ensure sidebar is expanded when opened on mobile
            this.isSidebarExpanded = true;
          } else {
            document.body.classList.remove('sidebar-open');
          }
        }
        this.cdr.markForCheck();
      }
    );

    this.dragAndDropArea = document.getElementById('drag-and-drop-area');
    this.documentUploadInput = document.getElementById('document-upload-input') as HTMLInputElement;

    this.dragAndDropArea?.addEventListener('click', () => this.documentUploadInput.click());

    this.dragAndDropArea?.addEventListener('dragover', (event) => {
      event.preventDefault();
      this.dragAndDropArea?.classList.add('dragover');
    });

    this.dragAndDropArea?.addEventListener('dragleave', () => {
      this.dragAndDropArea?.classList.remove('dragover');
    });

    this.dragAndDropArea?.addEventListener('drop', (event) => {
      event.preventDefault();
      this.dragAndDropArea?.classList.remove('dragover');
      const files = event?.dataTransfer!.files;
      this.documentUploadInput.files = files;
      // Handle the files
    });

    this.documentUploadInput.addEventListener('change', (event) => {
      const files = (event.target as HTMLInputElement).files;
      // Handle the files
    });
  }

  toggleSidebar(): void {
    this.isSidebarExpanded = !this.isSidebarExpanded;
    // Save state to localStorage
    this.saveSidebarState();
  }

  private loadSidebarState(): void {
    try {
      const savedState = localStorage.getItem(this.SIDEBAR_STATE_KEY);
      if (savedState !== null) {
        // Parse the saved state (it should be 'true' or 'false' as string)
        this.isSidebarExpanded = savedState === 'true';
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Error loading sidebar state from localStorage:', error);
      // If there's an error, use default value (expanded)
      this.isSidebarExpanded = true;
    }
  }

  private saveSidebarState(): void {
    try {
      localStorage.setItem(this.SIDEBAR_STATE_KEY, this.isSidebarExpanded.toString());
    } catch (error) {
      console.error('Error saving sidebar state to localStorage:', error);
    }
  }

  isActiveRoute(route: string | undefined): boolean {
    return route ? this._router.url === route : false;
  }

  private isEvaluator(): boolean {
    const role = (this.userService.user?.Role || '').toString().trim();
    return role === this.EVALUATOR_ROLE || role.toUpperCase() === this.EVALUATOR_ROLE.toUpperCase();
  }

  /**
   * Controls visibility per menu item based on user role.
   * - Evaluator: sees ONLY Evaluations + Logout.
   * - Procurement Officer: sees Dashboard, Manage Vendors, Tenders, Practices, and FWA Practices.
   * - Restricted roles: see only items with showForRestrictedRoles === true + Logout.
   * - Others: see all items unless a menu item has an explicit allowlist.
   */
  isMenuItemVisible(item: { label: string; showForRestrictedRoles: boolean; allowedRoles?: string[] }): boolean {
    const userRole = this.getNormalizedRole();

    if (item.label.toLowerCase() === 'logout') {
      return true;
    }

    if (item.label.toLowerCase() === 'dashboard') {
      return true;
    }

    if (this.isProcurementOfficerRole(userRole)) {
      return this.PROCUREMENT_OFFICER_MENU.has(item.label);
    }

    if (item.allowedRoles && item.allowedRoles.length > 0) {
      return item.allowedRoles.some(role => this.normalizeRole(role) === userRole);
    }

    if (this.isEvaluator()) {
      return item.label === 'Evaluations';
    }

    if (this.RESTRICTED_ROLES.some(role => this.normalizeRole(role) === userRole)) {
      return item.showForRestrictedRoles === true;
    }

    return true;
  }

  private getNormalizedRole(): string {
    return this.normalizeRole(this.userService.user?.Role || '');
  }

  private normalizeRole(role: any): string {
    return (role || '').toString().trim().toUpperCase().replace(/\s+/g, '');
  }

  private isProcurementOfficerRole(normalizedRole: string): boolean {
    return this.PROCUREMENT_OFFICER_ROLES.includes(normalizedRole);
  }

  handleButtonClick(event: Event,btn : string): void {
    if (btn && btn.toLowerCase() === 'logout') {
      // Always prevent navigation for logout button
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // Close sidebar on mobile if open
      if (window.innerWidth < 992) {
        this.sidebarService.closeMobileSidebar();
      }
      // Show confirmation dialog
      this.dialogService.showDialog(
        'Logout',  // Title
        'Are you sure you want to logout?',  // Content
        IconType.Warning,  // Icon type
        true,  // Show confirm button
        this.LogOut.bind(this),  // Pass the logout function to be executed
        true   // Show close button
      );
      return;
    }
    if (!this.isSidebarExpanded) {
      event.preventDefault();
    }
  }

  async LogOut() {
    try {
      // Hide dialog first
      this.dialogService.hideDialog();
      
      // Stop SignalR connection if active
      if (this.signalRService?.isConnected()) {
        await this.signalRService.stopConnection();
      }
      
      // Clear minimized meetings (toaster)
      this.minimizedMeetingService.clearAllMinimizedMeetings();
      
      // Clear user data and token
      this.userService.logout();
      
      // Navigate to login
      this._router.navigate([AppConstants.ROUTES.AUTHENTICATION, AppConstants.ROUTES.LOGIN]);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  handleSendMessage(messageContent: string) {
    console.log('Message Sent:', messageContent);
    // Add logic to send the message to the backend or process the message.
  }

  getIconPath(icon: string): string {
    // Check if icon already has extension
    if (icon.endsWith('.png') || icon.endsWith('.svg')) {
      return `assets/icons/${icon}`;
    }
    // Default to PNG
    return `assets/icons/${icon}.png`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up event listeners
    if (this.dragAndDropArea) {
      this.dragAndDropArea.removeEventListener('click', () => {});
      this.dragAndDropArea.removeEventListener('dragover', () => {});
      this.dragAndDropArea.removeEventListener('dragleave', () => {});
      this.dragAndDropArea.removeEventListener('drop', () => {});
    }
    if (this.documentUploadInput) {
      this.documentUploadInput.removeEventListener('change', () => {});
    }
  }

  // Close sidebar when clicking on a link (mobile only)
  onSidebarLinkClick(): void {
    if (window.innerWidth < 992) {
      this.sidebarService.closeMobileSidebar();
    }
  }

  // Close sidebar when clicking outside (mobile only)
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (window.innerWidth < 992 && this.isMobileSidebarOpen) {
      const sidebar = document.getElementById('sidebar');
      const hamburgerBtn = document.querySelector('.hamburger-btn');
      
      if (sidebar && hamburgerBtn) {
        const isClickInsideSidebar = sidebar.contains(event.target as Node);
        const isClickOnHamburger = hamburgerBtn.contains(event.target as Node);
        
        if (!isClickInsideSidebar && !isClickOnHamburger) {
          this.sidebarService.closeMobileSidebar();
        }
      }
    }
  }

  // Close sidebar when window is resized to desktop size
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event): void {
    if (window.innerWidth >= 992) {
      if (this.isMobileSidebarOpen) {
        this.sidebarService.closeMobileSidebar();
        document.body.classList.remove('sidebar-open');
      }
      // Restore saved sidebar state when switching to desktop
      this.loadSidebarState();
    }
  }
  
}
